import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { extractErrorMessage } from '@libs/shared/utils/functions/error-message.util';
import { Router } from '@angular/router';
import {
  CreateExpertRequest,
  ExpertRecord,
  ExpertService,
  UpdateExpertRequest,
  VerifyExpertPhoneNumberRequest,
  WebColorScheme,
} from '@libs/shared/api';
import { BasicSnackbarService } from '@libs/shared/ui/basic-snackbar/basic-snackbar.service';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, Subject, switchMap, tap } from 'rxjs';

interface ExpertCoreState {
  content: ExpertRecord;
  isLoading: boolean;
  isLoaded: boolean;
  errorMessage: string;
}

const initialState: ExpertCoreState = {
  content: undefined,
  isLoading: false,
  isLoaded: false,
  errorMessage: '',
};

@Injectable({
  providedIn: 'root',
})
export class CurrentExpertCoreStore {
  readonly #expertService = inject(ExpertService);
  readonly #basicSnackbarService = inject(BasicSnackbarService);
  readonly #router = inject(Router);

  readonly state$$ = signalState(initialState);
  readonly content$$ = this.state$$.content;
  readonly isLoading$$ = this.state$$.isLoading;
  readonly isLoaded$$ = this.state$$.isLoaded;
  readonly errorMessage$$ = this.state$$.errorMessage;

  constructor() {}

  readonly created$ = new Subject<ExpertRecord>();
  readonly updated$ = new Subject<ExpertRecord>();

  fetchExpertById = rxMethod<{
    id: string;
    eventType?: 'created' | 'updated';
  }>(
    pipe(
      switchMap(payload =>
        this.#expertService.fetchExpertById(payload.id).pipe(
          tapResponse(
            (expert: ExpertRecord) => {
              patchState(this.state$$, {
                content: expert,
                isLoading: false,
                isLoaded: true,
                errorMessage: '',
              });

              switch (payload.eventType) {
                case 'created':
                  this.created$.next(expert);
                  break;
                case 'updated':
                  this.updated$.next(expert);
                  break;
              }
            },
            (e: HttpErrorResponse) => {
              if (e.status === 404) {
                this.#router.navigate(['/on-boarding']);
              }
              patchState(this.state$$, {
                content: undefined,
                isLoading: false,
                isLoaded: false,
                errorMessage: extractErrorMessage(e),
              });
            }
          )
        )
      )
    )
  );

  createExpert = rxMethod<{
    request: CreateExpertRequest;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      tap(() => {
        patchState(this.state$$, {
          isLoading: true,
        });
      }),
      switchMap(payload =>
        this.#expertService.createExpert(payload.request).pipe(
          tapResponse(
            (expertId: string) => {
              if (payload.showSuccessSnackbar) {
                this.#basicSnackbarService.open('코치 매니저가 생성 되었어요.');
              }
              this.fetchExpertById({
                id: expertId,
                eventType: 'created',
              });
            },
            (e: HttpErrorResponse) => {
              if (payload.showFailureSnackbar) {
                this.#basicSnackbarService.open(
                  '코치 매니저 생성에 실패했어요.'
                );
              }
              patchState(this.state$$, {
                content: undefined,
                isLoading: false,
                isLoaded: false,
                errorMessage: extractErrorMessage(e),
              });
            }
          )
        )
      )
    )
  );

  updateExpert = rxMethod<{
    id: string;
    request: UpdateExpertRequest;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      tap(() => {
        patchState(this.state$$, {
          isLoading: true,
        });
      }),
      switchMap(payload =>
        this.#expertService.updateExpert(payload.id, payload.request).pipe(
          tapResponse(
            (expertId: string) => {
              if (payload.showSuccessSnackbar) {
                this.#basicSnackbarService.open('개인 정보가 수정 되었어요.');
              }
              this.fetchExpertById({
                id: expertId,
                eventType: 'updated',
              });
            },
            (e: HttpErrorResponse) => {
              if (payload.showFailureSnackbar) {
                this.#basicSnackbarService.open('개인 정보 수정에 실패했어요.');
              }
              patchState(this.state$$, {
                content: undefined,
                isLoading: false,
                isLoaded: false,
                errorMessage: extractErrorMessage(e),
              });
            }
          )
        )
      )
    )
  );

  updateExpertWebColorScheme = rxMethod<{
    id: string;
    webColorScheme: WebColorScheme;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      tap(() => {
        patchState(this.state$$, {
          isLoading: true,
        });
      }),
      switchMap(payload =>
        this.#expertService
          .updateExpertWebColorScheme(payload.webColorScheme)
          .pipe(
            tapResponse(
              () => {
                if (payload.showSuccessSnackbar) {
                  switch (payload.webColorScheme) {
                    case WebColorScheme.Light:
                      this.#basicSnackbarService.open(
                        '라이트 모드로 변경되었어요.'
                      );
                      break;
                    case WebColorScheme.Dark:
                      this.#basicSnackbarService.open(
                        '다크 모드로 변경되었어요.'
                      );
                      break;
                    case WebColorScheme.Auto:
                      this.#basicSnackbarService.open(
                        '자동 모드로 변경되었어요.'
                      );
                      break;
                  }
                }
                this.fetchExpertById({
                  id: payload.id,
                  eventType: 'updated',
                });
              },
              (e: HttpErrorResponse) => {
                if (payload.showFailureSnackbar) {
                  this.#basicSnackbarService.open('문제가 발생했어요! 😥');
                }
                patchState(this.state$$, {
                  content: undefined,
                  isLoading: false,
                  isLoaded: false,
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );

  verifyExpertPhoneNumber = rxMethod<{
    id: string;
    request: VerifyExpertPhoneNumberRequest;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      tap(() => {
        patchState(this.state$$, {
          isLoading: true,
        });
      }),
      switchMap(payload =>
        this.#expertService
          .verifyExpertPhoneNumber(payload.id, payload.request)
          .pipe(
            tapResponse(
              () => {
                if (payload.showSuccessSnackbar) {
                  this.#basicSnackbarService.open('전화번호가 인증되었어요.');
                }
                this.fetchExpertById({
                  id: payload.id,
                  eventType: 'updated',
                });
              },
              (e: HttpErrorResponse) => {
                if (payload.showFailureSnackbar) {
                  this.#basicSnackbarService.open('문제가 발생했어요! 😥');
                }
                patchState(this.state$$, {
                  content: undefined,
                  isLoading: false,
                  isLoaded: false,
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );
}
